import { useEffect, type RefObject } from 'react';
import { useDetailViewsQuota } from '@/access/quotaStore';
import { track } from '@/analytics/tracker';
import { useAuth } from '@/auth/AuthProvider';
import { usePaywallPrice } from '@/billing/usePaywallPrice';
import { useT } from '@/i18n/LocaleProvider';
import { cx } from '@/lib/cx';
import styles from './Catalog.module.css';
import type { ActiveChip } from './useFilterGroups';

/**
 * Баннер бесплатного плана (CAT-13); на Upgrade — цена плана окна по умолчанию, когда тарифы
 * пришли. Остаток просмотров кончился — на мобильном цену несёт счётчик в углу, а у
 * баннера её нет: в поле зрения одна подпись цены.
 */
export function FreeBanner({
  onUpgrade,
  onDismiss,
}: {
  onUpgrade: () => void;
  onDismiss: () => void;
}) {
  const t = useT();
  const price = usePaywallPrice();
  const { user } = useAuth();
  const quota = useDetailViewsQuota() ?? user?.quota?.detail_views ?? null;
  useEffect(() => {
    track('banner_view', { metadata: { banner: 'free_tier' } });
  }, []);
  return (
    <div className={styles.banner}>
      <span className={styles.bannerText}>
        <strong>{t('catalog.freePlan')}</strong> {t('catalog.freeNote')}
      </span>
      <button type="button" className={styles.bannerUpgrade} onClick={onUpgrade}>
        {t('nav.upgrade')}
        {price ? (
          <>
            {' '}
            <span
              className={cx(
                styles.bannerPrice,
                quota?.remaining === 0 && styles.bannerPriceDesktop,
              )}
            >
              {price}
            </span>
          </>
        ) : null}
      </button>
      <button
        type="button"
        className={styles.bannerClose}
        aria-label={t('common.dismiss')}
        onClick={() => {
          track('banner_dismiss', { metadata: { banner: 'free_tier' } });
          onDismiss();
        }}
      >
        ×
      </button>
    </div>
  );
}

/** Активные значения фильтров с удалением по одному (CAT-5). */
export function ActiveFilters({ chips }: { chips: ActiveChip[] }) {
  const t = useT();
  if (chips.length === 0) return null;
  return (
    <div className={styles.active} aria-label={t('catalog.activeFilters')}>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          className={styles.activeChip}
          onClick={chip.onRemove}
          aria-label={t('catalog.removeFilter', { label: chip.label })}
        >
          {chip.label}
          <span aria-hidden="true">×</span>
        </button>
      ))}
    </div>
  );
}

/** Одноразовая мобильная подсказка про long-press. */
export function LongPressHint({ onDismiss }: { onDismiss: () => void }) {
  const t = useT();
  return (
    <div className={styles.hint}>
      <span className={styles.hintRing} aria-hidden="true">
        <span className={styles.hintDot} />
      </span>
      <span className={styles.hintText}>
        <strong>{t('catalog.holdCard')}</strong>
        {t('catalog.holdCardRest')}
      </span>
      <button
        type="button"
        className={styles.hintClose}
        aria-label={t('common.dismiss')}
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
}

/** Блок лимита free-tier под сеткой (CAT-12). */
export function CapBlock({
  cap,
  onShowMore,
  innerRef,
}: {
  cap: number | null;
  onShowMore: () => void;
  innerRef: RefObject<HTMLDivElement>;
}) {
  const t = useT();
  return (
    <div ref={innerRef} className={styles.cap}>
      <span className={styles.capText}>
        {/* Числа о размере каталога наружу не выходят (ADR-013) — блок говорит «ещё есть», не «сколько». */}
        <strong>{t('catalog.moreResults')}</strong>
        {cap !== null ? t('catalog.freeCap', { n: cap }) : ''}
      </span>
      <button type="button" className={styles.capButton} onClick={onShowMore}>
        {t('catalog.showMore')}
      </button>
    </div>
  );
}

/** Пустая выдача с предложением запросить референс (CAT-14). */
export function EmptyState({
  query,
  didYouMean,
  onPickSuggestion,
  onRequest,
}: {
  query: string;
  /** Подсказка поиска при пустой выдаче («возможно, вы имели в виду»). */
  didYouMean?: string | null;
  onPickSuggestion?: (query: string) => void;
  onRequest: () => void;
}) {
  const t = useT();
  return (
    <div className={styles.empty}>
      <h3 className={styles.emptyTitle}>{t('catalog.emptyTitle')}</h3>
      {didYouMean && onPickSuggestion ? (
        <p className={styles.emptyText}>
          {t('catalog.didYouMean')}{' '}
          <button
            type="button"
            className={styles.inlineLink}
            onClick={() => onPickSuggestion(didYouMean)}
          >
            {didYouMean}
          </button>
          ?
        </p>
      ) : null}
      <p className={styles.emptyText}>{t('catalog.emptyText', { query })}</p>
      <button type="button" className={styles.ghost} onClick={onRequest}>
        {t('catalog.requestReference')}
      </button>
    </div>
  );
}

/**
 * Заголовок зоны выдачи поиска v3 (ADR-009): «Точные совпадения» / «Близко по смыслу».
 * Зона называется, но не считается — числа о количестве референсов наружу не выходят (ADR-013).
 */
export function ZoneHeading({ label }: { label: string }) {
  return <h2 className={styles.zoneHeading}>{label}</h2>;
}

/** Точных совпадений нет — честно говорим об этом и показываем близкое по смыслу. */
export function NoExactBanner({ query }: { query: string }) {
  const t = useT();
  return (
    <div className={styles.noExact} role="status">
      <strong className={styles.noExactTitle}>{t('catalog.noExactTitle', { query })}</strong>
      <span className={styles.noExactText}>{t('catalog.noExactText')}</span>
    </div>
  );
}

/** Поиск шёл не по набранному тексту (перевод, исправление, дописанное слово) — показываем, по какому. */
export function InterpretedAs({ value }: { value: string }) {
  const t = useT();
  return (
    <p className={styles.interpreted}>
      {t('catalog.showingResultsFor')} <strong>{value}</strong>
    </p>
  );
}

/** Запрос равен имени категории: поиск идёт как обычно, а категорию целиком можно открыть одним нажатием. */
export function CategoryHint({ name, onShow }: { name: string; onShow: () => void }) {
  const t = useT();
  return (
    <button type="button" className={styles.categoryHint} onClick={onShow}>
      {t('catalog.showCategory', { name })}
    </button>
  );
}

export function LoadingLabel({ text }: { text: string }) {
  return <p className={styles.loading}>{text}</p>;
}

/** Ошибка загрузки выдачи — тексты вне прототипа, см. журнал расхождений. */
export function ErrorBlock({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useT();
  return (
    <div className={styles.empty} role="alert">
      <h3 className={styles.emptyTitle}>{t('catalog.errorTitle')}</h3>
      <p className={styles.emptyText}>{message}</p>
      <button type="button" className={styles.ghost} onClick={onRetry}>
        {t('common.tryAgain')}
      </button>
    </div>
  );
}
